import { MutationFunction } from "@apollo/client";
import backIcon from "@assets/images/back.svg";
import documentAddIcon from "@assets/images/document-add.svg";
import printerIcon from "@assets/images/printer.svg";
import switchHorizontalIcon from "@assets/images/swap-horizontal.svg";
import { Button, Paper } from "@material-ui/core";
import { notRefchangeOrderPath } from "@saleor/changeOrderDomestic/urls";
import DecimalFormat from "@saleor/components/DecimalFormat";
import MyTypography from "@saleor/components/MyTypography";
import { createOrderNotRefUrl } from "@saleor/createOrChangeNotRefContract/urls";
import {
  DownloadOrdersPdfMutation,
  DownloadOrdersPdfMutationVariables,
  PreviewDomesticOrderQuery
} from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import useNavigator from "@saleor/hooks/useNavigator";
import { formatThousandDecimal } from "@saleor/utils/number";
import React, { FC } from "react";
import InlineSVG from "react-inlinesvg";
import { FormattedMessage, useIntl } from "react-intl";

import useStyles from "./styles";
export interface PreviewOrderNotRefFooterProps {
  onDownloadPdf: MutationFunction<
    DownloadOrdersPdfMutation,
    DownloadOrdersPdfMutationVariables
  >;
  previewOrder?: PreviewDomesticOrderQuery;
}

const PreviewOrderNotRefFooter: FC<PreviewOrderNotRefFooterProps> = ({
  onDownloadPdf,
  previewOrder
}) => {
  const [appState, _] = useAppState();
  const navigate = useNavigator();
  const classes = useStyles({
    expandMenu: appState.expandMenu,
    bodyPaddingRight: appState.bodyPaddingRight
  });
  const intl = useIntl();

  const handleDownloadFile = async () => {
    const { data } = await onDownloadPdf({
      variables: {
        soNo: previewOrder?.previewDomesticOrder?.previewHeaderData?.soNo
      }
    });
    if (
      data?.downloadOrdersPdf?.exportedFileBase64 &&
      data.downloadOrdersPdf?.fileName
    ) {
      const link = document.createElement("a");
      link.href = `data:application/pdf;base64,${data.downloadOrdersPdf.exportedFileBase64}`;
      link.download = data.downloadOrdersPdf.fileName;
      link.target = "_self";
      link.click();
    }
  };

  return (
    <div className={classes.menu}>
      <Paper className={classes.wrapper}>
        <div className={classes.section}>
          <div>
            <MyTypography
              variant="subtitle2"
              className={classes.summaryCaption}
            >
              {intl.formatMessage({
                defaultMessage: "สรุปยอดคำสั่งซื้อ"
              })}
            </MyTypography>
            <MyTypography variant="body1" className={classes.number}>
              {`(${
                previewOrder?.previewDomesticOrder?.previewItemData?.length
              } ${intl.formatMessage({
                defaultMessage: "items"
              })})`}
            </MyTypography>
          </div>
          <div>
            <div className={classes.price}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {intl.formatMessage({
                  defaultMessage: "ยอดรวมสุทธิ"
                })}
              </MyTypography>
              <MyTypography variant="body1" className={classes.number}>
                {previewOrder?.previewDomesticOrder?.previewFooterData
                  ?.netTotalPrice ? (
                  <DecimalFormat
                    value={
                      previewOrder?.previewDomesticOrder?.previewFooterData
                        ?.netTotalPrice
                    }
                    isGrayDecimal={false}
                    countDecimal={2}
                  />
                ) : (
                  "0.00"
                )}
              </MyTypography>
            </div>
            <div className={classes.price}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {intl.formatMessage({
                  defaultMessage: "ภาษีมูลค่าเพิ่ม 7%"
                })}
              </MyTypography>
              <MyTypography variant="body1" className={classes.number}>
                {previewOrder?.previewDomesticOrder?.previewFooterData
                  ?.totalVat ? (
                  <DecimalFormat
                    value={
                      previewOrder?.previewDomesticOrder?.previewFooterData
                        ?.totalVat
                    }
                    isGrayDecimal={false}
                    countDecimal={2}
                  />
                ) : (
                  "0.00"
                )}
              </MyTypography>
            </div>
            <div className={classes.price}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {intl.formatMessage({
                  defaultMessage: "ยอดรวมภาษีมูลค่าเพิ่ม"
                })}
              </MyTypography>
              <MyTypography variant="subtitle1" className={classes.number}>
                <span className={classes.netPriceNumber}>
                  {previewOrder?.previewDomesticOrder?.previewFooterData
                    ?.orderAmountAfterVat
                    ? formatThousandDecimal(
                        previewOrder?.previewDomesticOrder?.previewFooterData
                          ?.orderAmountAfterVat,
                        2
                      )
                    : "0.00"}
                </span>
              </MyTypography>
            </div>
            <div className={classes.priceCurrency}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {intl.formatMessage({
                  defaultMessage: "Currency"
                })}
              </MyTypography>
              <MyTypography variant="body1" className={classes.currency}>
                {
                  previewOrder?.previewDomesticOrder?.previewFooterData
                    ?.currency
                }
              </MyTypography>
            </div>
          </div>
        </div>
        <hr className={classes.divider} />
        <div className={classes.sectionBtn}>
          <div className={classes.sectionBtnLeft}>
            <Button
              id="backToHomeButton"
              variant="contained"
              color="secondary"
              onClick={() => navigate("/")}
              className={classes.btnConfirmOrder}
              startIcon={<InlineSVG src={backIcon} />}
            >
              <MyTypography variant="body1">
                <FormattedMessage defaultMessage="กลับหน้าหลัก" />
              </MyTypography>
            </Button>
            <Button
              id="printButton"
              variant="outlined"
              color="secondary"
              className={classes.btnAddProduct}
              onClick={handleDownloadFile}
              startIcon={<InlineSVG src={printerIcon} />}
            >
              <MyTypography variant="body1">
                <FormattedMessage defaultMessage="พิมพ์เอกสาร" />
              </MyTypography>
            </Button>
          </div>

          <div className={classes.sectionBtnLeft}>
            <Button
              id="createOrderButton"
              variant="contained"
              color="secondary"
              onClick={() => navigate(`${createOrderNotRefUrl}`)}
              className={classes.btnChangeOrder}
              startIcon={<InlineSVG src={documentAddIcon} />}
            >
              <FormattedMessage defaultMessage="Create Order" />
            </Button>
            <Button
              id="changeOrderButton"
              variant="contained"
              color="secondary"
              onClick={() =>
                navigate(
                  notRefchangeOrderPath(
                    previewOrder?.previewDomesticOrder?.previewHeaderData?.soNo
                  )
                )
              }
              className={classes.btnChangeOrder}
              startIcon={<InlineSVG src={switchHorizontalIcon} />}
            >
              <FormattedMessage defaultMessage="Change Order" />
            </Button>
          </div>
        </div>
      </Paper>
    </div>
  );
};

export default PreviewOrderNotRefFooter;
